import * as React from 'react';

import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import './ribbon.css';
import { useLocation } from 'react-router';
import { useMediaQuery } from '@mui/system';
import { Box, Theme, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { FlexyLayoutState } from '@europrocurement/flexy-components/redux/storeConfig/store';
import { MenuContext, MenuContextType } from '@europrocurement/flexy-components/providers';
import { L2DFSidebar, L2DFSidebarProps } from '../L2DFSidebar';
import type { MenuEntry } from './MenuEntry';
import { MenuItemProps } from '../../molecules/MenuItem';
import { useSidebar } from './useSidebar';
import { useFullLayout } from '../../templates/FullLayout/FullLayoutContext';

export type SidebarProps = {
    menuEntries: MenuEntry[];
    logo: React.ReactNode;
    logoFolded: React.ReactNode;
    actions?: L2DFSidebarProps['actions'];
};

export const Sidebar: React.FunctionComponent<SidebarProps> = function ({
    menuEntries,
    logo,
    logoFolded,
    actions = [],
}: SidebarProps) {
    const { isSidebarOpen, setSidebarOpen } = useFullLayout();

    const kc = UseKeycloakService();
    const theme = useTheme();
    const smBreakpoint = useMediaQuery((t: Theme) => t.breakpoints.up('sm'));
    const folded = !isSidebarOpen;
    const { entryToItem, findActiveElements, findOpenElements } = useSidebar();

    const filterEntries = (entries: MenuEntry[]): MenuItemProps[] =>
        entries
            .filter((entry) => !entry.role || kc.checkRole(entry.role, true))
            .map((entry) => ({
                ...entryToItem(entry, theme.palette.primary, folded),
                childrenItems: entry.children ? filterEntries(entry.children) : [],
            }));

    const menuItems = filterEntries(menuEntries);

    const { pathname } = useLocation();
    const env = useSelector((state: FlexyLayoutState) => state.customizer.env);
    const [activeTree, setActiveTree] = React.useState<Array<string>>(
        findActiveElements(menuItems),
    );
    const [openElements, setOpenElements] = React.useState<Array<string>>(
        findOpenElements(menuItems),
    );

    // Synchronise l'élément actif du menu avec l'URL courante, y compris lors
    // d'une navigation programmatique (navigate()) qui ne passe pas par un clic
    // sur le menu.
    React.useEffect(() => {
        setActiveTree(findActiveElements(menuItems));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);
    const menuContextValue: MenuContextType = React.useMemo(
        () => ({
            activeTree,
            setActiveTree,
            openElements,
            setOpenElements,
        }),
        [activeTree, setActiveTree, openElements, setOpenElements],
    );

    if (!isSidebarOpen && !smBreakpoint) {
        return null;
    }

    return (
        <Box data-testid="test-id-Sidebar">
            {env !== 'prod' ? (
                <Box
                    id="env-ribbon"
                    className={`${env}${folded ? ' folded' : ''}`}
                >
                    {env}
                </Box>
            ) : null}
            <MenuContext.Provider value={menuContextValue}>
                <L2DFSidebar
                    actions={actions}
                    logo={logo}
                    logoFolded={logoFolded}
                    items={menuItems}
                    folded={folded}
                    onClose={() => setSidebarOpen(false)}
                />
            </MenuContext.Provider>
        </Box>
    );
};
